<template>
  <div id="reptree-container" :class="{ 'reptree-container-background-light' : !isDark,
                    'reptree-container-background-dark' : isDark}">
    <div v-for="line in displayLines" class="reptree-line-height" v-bind:class="line.indentString" v-bind:key="line.dummyIndex" >
      <span v-for="move in line.movesOnLine" v-bind:key="move.index">
        <span @click="onClick(line.index,move.index)"
                :class="{
                  'reptree-selected-text-dark' : (move.selected || move.highlighted) && isDark,
                  'reptree-move-text-dark'     : (!(move.selected || move.highlighted)) && isDark,
                  'reptree-selected-text-light' : (move.selected || move.highlighted) && !isDark,
                  'reptree-move-text-light'     : (!(move.selected || move.highlighted)) && !isDark,
                  'reptree-move-italic'         : (move.fenid > 1 && move.fenid < 10000000)
                }">
          {{ move.displaySan }} 
          <i v-if="showComments && move.comment != ''" class="reptree-move-comment">{{  "{ " + move.comment + " }" }}</i>
        </span>
      
        <span @click="onFlagClick(line.index,move.index,true)">
          {{ move.branchFlag }}
        </span>
        &nbsp;
      </span>
    </div>
  </div>
</template>

<script setup>

// props -> "slot"

// emit ->  playingwhite so parent can flip board
//          repertoirename so it can go in the comment (commentString.value = repertoireName.value)
//          toolbar status

import { buildPieceLocationStrings, getPieceSetName, columnToNumber, squarePairToAlgebraic } from '@/composables/blindfoldSupport.js'


import { ref, watch } from 'vue'
import { computed } from "@vue/reactivity"

import { useGetData } from '@/composables/getData.js'
import { useConnectionStore } from '@/stores/connectionStore';

// repertoire-comment : the comment needs to be displayed
// repertoire-info    : initial stuff about the repertoire
// -toolbar-update : the < > disabled/enable flags
// move-click : a move has been clicked on
// branch-click : a branch has been clicked on

const emit = defineEmits(['repertoire-comment','repertoire-info',
                  'toolbar-update','move-click','branch-click'])

// reloadRepertoire - get repertoire from server again
// fenidPath - comma separated set of fenid to highlight. show last one on board
// calledFrom - which screen has the tree displayed.  "tree", "practice",
//       builder will use -> "static", "dynamic", "bidirectional"
// moveDirection - positive or negative to move forward or backward

const props = defineProps({
  reloadRepertoire : {
    type : Number,
    default : 0
  },
  fenidPath : {
    type : String,
    default : null
  },
  calledFrom : {
    type : String,
    default : "tree"
  },
  repertoireID : {
    type : String,
    default : ""
  },
  moveDirection : {
    type : Number,      
    default : 0
  },
  showComments : {
    type : Boolean,
    default : false
  }
})

const connectionStore = useConnectionStore()

const isDark = ref(connectionStore.getDarkmode != "" && connectionStore.getDarkmode != "light")

const displayLines = ref([])
var arrowPairs = []

var selectedLineIndex = -1
var selectedMoveOnLineIndex = -1
var playingWhite = 0
var empty = 0
var lines = []
var dummyIndex = 0

var respondToClicks = (props.calledFrom == "tree" || props.calledFrom == "bidirectional" || props.calledFrom == "force")
var highlightBranches = (props.calledFrom == "tree" || props.calledFrom == "force")

var repertoireName = ""

//console.log("in reptree")
getRepTree()

const calledFrom = computed({ get() { return props.calledFrom }})
const fenidPath = computed({ get() { return props.fenidPath }})
const moveDirection = computed({ get() { return props.moveDirection }})
const reloadRepertoire = computed({ get() { return props.reloadRepertoire }})
const showComments = computed({ get() { return props.showComments }})

watch(reloadRepertoire, (newVal,oldVal) => {
  //console.log('*********** reload repertoire changed',newVal)
  getRepTree()
})

watch(fenidPath, (newVal,oldVal) => {
  //console.log('*********** fenidPath changed',newVal)
  if (newVal == null)
    return
  const fenidArray = newVal.split(",")
  zapSelections()
  highlightFenidPath(fenidArray)

  //console.log("after highlight ",displayLines.value)
})

watch(calledFrom, (newVal,oldVal) => {
  //console.log('*********** calledfrom changed',newVal,props.calledFrom)

  respondToClicks = (props.calledFrom == "tree" || props.calledFrom == "bidirectional" || props.calledFrom == "force")
  highlightBranches = (props.calledFrom == "tree" || props.calledFrom == "force")

})

watch(showComments, (newVal,oldVal) => {
  //console.log("Show comments",newVal)
})

watch(moveDirection, (newVal,oldVal) => {
    //console.log('*********** moveDirection changed',newVal)
    zapSelections()

    if (newVal < 0)
      selectedMoveOnLineIndex--
    else
      selectedMoveOnLineIndex++

    lines[selectedLineIndex].movesOnLine[selectedMoveOnLineIndex].selected = true

    emit("move-click",lines[selectedLineIndex].movesOnLine[selectedMoveOnLineIndex].fen,null)
    emit("repertoire-comment",lines[selectedLineIndex].movesOnLine[selectedMoveOnLineIndex].comment)

    setToolbarStatus()
    buildDisplayLines()

  })

function initializeTree() {
  arrowPairs = []

  selectedLineIndex = -1
  selectedMoveOnLineIndex = -1
  playingWhite = 0
  empty = 0
  lines = []
}

function setToolbarStatus() {
    var backDisabled = false
    var forwardDisabled = false
    if (selectedLineIndex == -1 || selectedMoveOnLineIndex == -1) {
      backDisabled = true
      forwardDisabled = true
    }
    else
    if (selectedMoveOnLineIndex == 0) {
      backDisabled = true
    }
    else
      if (lines[selectedLineIndex].movesOnLine.length -1 <= selectedMoveOnLineIndex )
        forwardDisabled = true
    
    //console.log("emit toolbar update",backDisabled,forwardDisabled)
    emit("toolbar-update",backDisabled,forwardDisabled)
    
  }

function buildDisplayLines() {
    var moveIsWhite = true
    var moveNumber = 0
    var displaySan = ""
    var indent = 0

    displayLines.value = []

    for (var lineIndex = 0; lineIndex < lines.length; lineIndex++) {
      var line = lines[lineIndex]
      if (line.isVisible == false)
        continue

      indent = line.lineIndent

      var isFirstMove = true
      if ((line.startingPly % 2) == 1) {
        moveIsWhite = false;
        moveNumber = Math.floor((line.startingPly-1) / 2) + 1;
      }
      else {
        moveIsWhite = true;
        moveNumber = line.startingPly/2 + 1;
      }
      var moves = []
      for (var moveIndex = 0; moveIndex < line.movesOnLine.length; moveIndex++) {
        var move = line.movesOnLine[moveIndex]

        if (isFirstMove) {
          const dotString = (moveIsWhite) ? "." : "..."
          displaySan = moveNumber + dotString + move.san
        }
        else {
          if (moveIsWhite)
            displaySan = moveNumber + "." + move.san
          else
            displaySan = move.san
        }
        if (moveIsWhite == false)
          moveNumber++
        moveIsWhite = !moveIsWhite
        isFirstMove = false

        const displayMove = {
          index : moves.length,
          moveOnLineIndex : move.index,
          displaySan : displaySan,
          comment : move.comment,
          selected : move.selected,
          highlighted : move.highlighted,
          branchFlag : move.branchFlag,
          fenid : move.fenid
        }

        moves.push(displayMove)

        if (move.branchFlag == "(-)") {
          // got to expand this line. push what has been done so far
          // put rest of moves on a new displayline
          const displayLine = {
            dummyIndex : dummyIndex++,
            index : displayLines.value.length,
            lineIndex : line.index,
            isVisible : line.isVisible,
            indent : indent,
            indentString : "indent" + indent,
            movesOnLine : moves
          }
          displayLines.value.push(displayLine)

          // zap the indentation for the "lines" that branch off here
          for (var openIndex = 0; openIndex < move.linesToOpen.length; openIndex++) {
            const lineToZap = move.linesToOpen[openIndex]
            lines[lineToZap].lineIndent = indent + 1
          }

          moves = []
          isFirstMove = true
          indent++;
        }
      }

      const displayLine = {
        dummyIndex : dummyIndex++,
        index : displayLines.value.length,
        lineIndex : line.index,
        isVisible : line.isVisible,
        indent : indent,
        indentString : "indent" + indent,
        movesOnLine : moves
      }
      displayLines.value.push(displayLine)
    }
    //console.log("**** DISPLAY LINES ",displayLines.value)
  }

  function processJsonResponse(json) {
    //console.log(" in callback",json)
    if (json.hasOwnProperty('error')) {
      alert("ERROR while running -> " + json['programRun'] + "\n\n" + json['error'])
      return;
    }

    //console.log("*** process Rep Tree ***")

    initializeTree()

    empty = json['empty']
    if (empty == 1) {
      selectedLineIndex = -1
      buildDisplayLines()
      setToolbarStatus()

      emit("repertoire-info", "empty" ,"No moves in repertoire")
      return
    }

    playingWhite = json['playing']
    repertoireName = json['repname']

    emit("repertoire-info", (playingWhite == 1) ? "white" : "black" ,repertoireName)

    const fenids = json['fenids'].split(",")
    const comments = json['comments'].split("\t")
    const branchFlags = json['flags'].split(",")
    const fens = json['fens'].split(",")

    // one of these for each move in the entire tree
    var moveInfoArray = []
    for (var i=0; i < branchFlags.length; i++) {

      var oneMove = {
        index : i, 
        flag: branchFlags[i],
        fen : fens[i], 
        fenid: fenids[i],
        comment : comments[i],
        onExplorerPath : false,
        branchFlag : (branchFlags[i] == 1) ? "(+)" : "",
        userFenid : (fenids[i].length < 9 && fenids[i] != "1"),
        linesToOpen : (branchFlags[i] == 1) ? json['open' + i].split(",") : ""
      }
      moveInfoArray.push(oneMove)
    }

    const lineCount = json['linecount']
    const startingPlys = json['starting_ply'].split(",")
    const parentLines = json['parent_line'].split(",")
    
    for (var i=0; i < lineCount; i++) {

      var movesOnLine = []
      const moveIndexsOnLine = json['line' + i].split(",")
      const sansOnLine = json['san' + i].split(",")
      const squarePairsOnLine = json['pair' + i].split(",")

      if (sansOnLine[sansOnLine.length - 1] == "zzzz")
        sansOnLine.pop()

      for (var j=0; j < sansOnLine.length; j++) {
        const moveIndex = moveIndexsOnLine[j]
        var move = {
          index : j,
          selected : false,
          highlighted : false,
          san : sansOnLine[j],
          displaySan : "",
          moveIndex : moveIndex,
          squarePair : squarePairsOnLine[j],
          fenid : moveInfoArray[moveIndex].fenid,
          fen : moveInfoArray[moveIndex].fen,
          comment : moveInfoArray[moveIndex].comment,
          branchFlag : moveInfoArray[moveIndex].branchFlag,
          userFenid : moveInfoArray[moveIndex].userFenid,
          linesToOpen : moveInfoArray[moveIndex].linesToOpen
        }
        movesOnLine.push(move)
      }

      const oneStartingPly = parseInt(startingPlys[i])
      var line = {
          index : i,
          isVisible : (oneStartingPly == 0),
          startingPly : oneStartingPly,
          lineIndent : 0,
          parentLine : parentLines[i],
          movesOnLine : movesOnLine

      }

      lines.push(line)
    }
    //console.log("***** LINES -> ",lines)
    buildDisplayLines()
    setToolbarStatus()

    // if the fenid array is there at start up, load it in

    //console.log("********** fenid path", props.fenidPath)
    if (props.fenidPath != null && props.fenidPath != "") {
      const fenidArray = props.fenidPath.split(",")
      highlightFenidPath(fenidArray)
    }
  }

  function zapSelections() {
    for (var i = 0; i < displayLines.value.length; i++) {
      const line = displayLines.value[i]
      //if (line.isVisible == false)
      //  continue
      for (var j=0; j < line.movesOnLine.length; j++) {
        line.movesOnLine[j].selected = false
        line.movesOnLine[j].highlighted = false
      }
    }
    for (var i = 0; i < lines.length; i++) {
      const line = lines[i]
      //if (line.isVisible == false)
      //  continue
      for (var j=0; j < line.movesOnLine.length; j++) {
        line.movesOnLine[j].selected = false
        line.movesOnLine[j].highlighted = false
      }
    }
  }

  function highlightFenidPath(fenidArray) {
    var mustRebuildDisplay = false

    //console.log("HIGHLIGHTFENID PATH",fenidArray)
    // walk the display lines and set the highlighting for the path

    for (var fenIndex = 0; fenIndex < fenidArray.length; fenIndex++) {
      const lookupFenid = fenidArray[fenIndex]
      //console.log("lookup to higlight ",lookupFenid)
      for (var i = 0; i < displayLines.value.length; i++) {
        const displayLine = displayLines.value[i]
 
        for (var j=0; j < displayLine.movesOnLine.length; j++) {
          if (displayLine.movesOnLine[j].fenid == lookupFenid) {
            //console.log("fenid match ",j,displayLine.isVisible)
            if (displayLine.isVisible == false) {
              displayLine.isVisible = true
              mustRebuildDisplay = true
            }
            displayLine.movesOnLine[j].highlighted = true

            // see if we need to open the lines
            var displayMove = displayLine.movesOnLine[j]
            if (displayMove.branchFlag == "(+)") {
              onFlagClick(i,j,false)
              mustRebuildDisplay = true
            }
          }
        }
      }

      // walk all the other lines
      for (var i = 0; i < lines.length; i++) {
        const line = lines[i]
        //console.log("line array ", line)

        //if (line.isVisible == false)
        //  continue
        for (var j=0; j < line.movesOnLine.length; j++) {
          if (line.movesOnLine[j].fenid == lookupFenid) {
            //console.log("fenid match on all lines ",j,line.isVisible)
            if (line.isVisible == false) {
              line.isVisible = true
              mustRebuildDisplay = true
            }
            line.movesOnLine[j].highlighted = true
          }
        }
      }
    }
    if (mustRebuildDisplay)
      buildDisplayLines()
  }

  // build the equivalent of the "Move History" as if the repertoire builder had made the sequence of moves
  //       need:   fen, fenid, san -- per move
  function buildMoveHistory(displayLineIndex,displayMoveIndex) {
    var moveHistoryArray = []
    var fenidArray = []

    const displayLine = displayLines.value[displayLineIndex]
    const displayMove = displayLine.movesOnLine[displayMoveIndex]

    var moveOnLineIndex = displayMove.moveOnLineIndex

    var line = lines[displayLine.lineIndex]
    //console.log("initial displayLine",displayLine)
    //console.log("initial displayMove",displayMove)
    //console.log("initial line",line)
    //console.log("initial move index",moveOnLineIndex)

    /*
    // go backward in the current displayline
    for (var i = displayMoveIndex; i >= 0; i--) {
      const  displayMove = displayLine.movesOnLine[i]
      //console.log("build fen list ",i,displayMove.fenid,displayMove)
    }
    */

    while (true) {
      for (var i = moveOnLineIndex; i >= 0; i--) {
        const  lineMove = line.movesOnLine[i]

        lineMove.highlighted = true

        //console.log("build fen list original line ",i,lineMove.fenid,lineMove)
        const oneMove = { 
                fen : lineMove.fen,
                fenid : lineMove.fenid,
                san : lineMove.san,
                comment : lineMove.comment
              }
        moveHistoryArray.push(oneMove)
        fenidArray.push(lineMove.fenid)
      }
      if (line.startingPly == 0)
        break;

      const currentPly = line.startingPly
      const parentLine = parseInt(line.parentLine)
      if (parentLine == -1) {
        //console.log("***** ran out of parents")
        break;
      }

      line = lines[parentLine]
      //console.log("PARENT LINE ",line)

      moveOnLineIndex = currentPly - line.startingPly - 1

      //console.log("in parent new moveOnLineIndex",currentPly, moveOnLineIndex)  
    }
    
    moveHistoryArray.reverse()
    fenidArray.reverse()
    
    buildDisplayLines()

    //console.log("before highlight",fenidArray)
    highlightFenidPath(fenidArray)
    //console.log("Fenid Array reversed",moveHistoryArray)
    return (moveHistoryArray)
  }

  function onClick(displayLineIndex,displayMoveIndex) {
    var moveHistoryArray = null

    //console.log("called from ", props.calledFrom)
 
    // if from practice, ignore
    if (respondToClicks == false)
      return

    // clear the previous selection from both display and raw lines

    zapSelections()
    var displayLine = displayLines.value[displayLineIndex]
    var displayMove = displayLine.movesOnLine[displayMoveIndex]
    displayLine.movesOnLine[displayMoveIndex].selected = true
    var lineIndex = displayLine.lineIndex
    var line = lines[lineIndex]
    line.movesOnLine[displayMove.moveOnLineIndex].selected = true

    if (props.calledFrom == "bidirectional") {
      //console.log("clicked for bidi ",displayLineIndex,displayMoveIndex)
      moveHistoryArray = buildMoveHistory(displayLineIndex,displayMoveIndex)
    }

    emit("move-click",line.movesOnLine[displayMove.moveOnLineIndex].fen,moveHistoryArray)
    emit("repertoire-comment",line.movesOnLine[displayMove.moveOnLineIndex].comment)

    selectedLineIndex = lineIndex
    selectedMoveOnLineIndex = displayMove.moveOnLineIndex

    if (arrowPairs.length > 0)
      arrowPairs = []

    setToolbarStatus()

  }

  function zapSubLines(linesToOpen) {
    //const linesToOpen = line.movesOnLine[i].linesToOpen
    for (var i1 = 0; i1 < linesToOpen.length; i1++) {
      var secondaryLine = lines[linesToOpen[i1]]
      lines[linesToOpen[i1]].isVisible = false
      for (var j=0; j < secondaryLine.movesOnLine.length; j++) {
        if (secondaryLine.movesOnLine[j].branchFlag == "(-)")
          secondaryLine.movesOnLine[j].branchFlag = "(+)"
        zapSubLines(secondaryLine.movesOnLine[j].linesToOpen)
      }
    }
  }

  function onFlagClick(displayLineIndex,displayMoveIndex,cameFromRealClick) {

    var displayLine = displayLines.value[displayLineIndex]
    var displayMove = displayLine.movesOnLine[displayMoveIndex]
    var lineIndex = displayLine.lineIndex
    var line = lines[lineIndex]

    var branchFlag = displayMove.branchFlag

    //console.log("flag click -> ", displayLineIndex,displayMoveIndex,branchFlag)

    // only zap if interacting
    if (respondToClicks && cameFromRealClick)
      zapSelections()

    arrowPairs = []

    // open up or close it down
    if (branchFlag == "(+)") {
      line.movesOnLine[displayMove.moveOnLineIndex].branchFlag = "(-)"

      if (respondToClicks)
        line.movesOnLine[displayMove.moveOnLineIndex].selected = true

      selectedLineIndex = lineIndex
      selectedMoveOnLineIndex = displayMove.moveOnLineIndex

      //console.log("*** next move ",line.movesOnLine[displayMove.moveOnLineIndex+1])
      if (highlightBranches)
        line.movesOnLine[displayMove.moveOnLineIndex+1].highlighted = true

      var pairString = squarePairToAlgebraic(line.movesOnLine[displayMove.moveOnLineIndex+1].squarePair)
      arrowPairs.push(pairString)

      emit('move-click',line.movesOnLine[displayMove.moveOnLineIndex].fen,null)

      if (line.movesOnLine[displayMove.moveOnLineIndex].linesToOpen != "") {
        const linesToOpen = line.movesOnLine[displayMove.moveOnLineIndex].linesToOpen
        for (var i = 0; i < linesToOpen.length; i++) {
          lines[linesToOpen[i]].isVisible = true

          if (highlightBranches)
            lines[linesToOpen[i]].movesOnLine[0].highlighted = true

          pairString = squarePairToAlgebraic(lines[linesToOpen[i]].movesOnLine[0].squarePair)
          arrowPairs.push(pairString)
        }
      }
    }
    else {
      //line.movesOnLine[displayMove.moveOnLineIndex].branchFlag = "(+)"
      line.movesOnLine[displayMove.moveOnLineIndex].selected = true
      emit('move-click',line.movesOnLine[displayMove.moveOnLineIndex].fen,null)

      selectedLineIndex = lineIndex
      selectedMoveOnLineIndex = displayMove.moveOnLineIndex

      for (var i = displayMove.moveOnLineIndex; i < line.movesOnLine.length; i++) {
        var secondaryDisplayMove = displayLine.movesOnLine[i]
        if (line.movesOnLine[i].branchFlag == "(-)") {
          line.movesOnLine[i].branchFlag = "(+)"

          const linesToOpen = line.movesOnLine[i].linesToOpen
          zapSubLines(linesToOpen)
          for (var i1 = 0; i1 < linesToOpen.length; i1++) {
            var secondaryLine = lines[linesToOpen[i1]]
            lines[linesToOpen[i1]].isVisible = false
            for (var j=0; j < secondaryLine.movesOnLine.length; j++)
              if (secondaryLine.movesOnLine[j].branchFlag == "(-)")
                secondaryLine.movesOnLine[j].branchFlag = "(+)"
          }
        }
      }
    }
    emit("branch-click",arrowPairs)

    if (cameFromRealClick)
      buildDisplayLines()
    setToolbarStatus()
  }

  function getRepTree() {
    //console.log("*** in getRepTree")
    var slotId = "edit"
    var repertoireID = ""

    if (props.calledFrom == "practice")
      slotId = "practice"
    else
    if (props.calledFrom == "force") {
      slotId = "force"
      repertoireID = props.repertoireID
    }
    var formData = {
      fenid_path : "",
      slot_id : slotId,
      id : repertoireID
    }
    //console.log("getweb rep tree formdata",formData)
    useGetData("chessvis_legacy_repertoire_tree.php", formData, processJsonResponse)
  }

</script>

<style scoped>

#reptree-container {
  position : relative;
}

.reptree-container-background-dark {
  background-color: #403726;
}

.reptree-container-background-light {
  background-color : #ebf2f2;
}

.reptree-move-text-light {
  color : rgb(13,110,253)
}
.reptree-move-text-dark {
  color : #fff;
}
.reptree-selected-text-light {
  color : blue;
  background : yellow;
  padding: 2px;
  border : solid 1px black;
  border-radius: 3px;
}

.reptree-selected-text-dark {
  color : #fff;
  background : gray;
  padding: 2px;
  border : solid 1px #fff;
  border-radius: 3px;
}

.reptree-move-italic {
  font-style: italic;
}

.reptree-move-comment {
  font-style: italic;
  font-size: .9em;
}

.reptree-line-height {
  line-height: 1.8em;
}

.indent0 {
  padding-left: 1em;
  text-indent:-1em;
}
.indent1 {
  padding-left: 2em;
  text-indent:-1em;
}

.indent2 {
  padding-left: 3em;
  text-indent:-1em;
}

.indent3 {
  padding-left: 4em;
  text-indent:-1em;
}
.indent4 {
  padding-left: 5em;
  text-indent:-1em;
}

.indent5 {
  padding-left: 6em;
  text-indent:-1em;
}

.indent6 {
  padding-left: 7em;
  text-indent:-1em;
}

.indent7 {
  padding-left: 8em;
  text-indent:-1em;
}


</style>